import type { ObjectType } from '../../../shared/types';

export type SelectionTreeScope =
  | 'objectType'
  | 'general'
  | 'processConditions'
  | 'projectProperties'
  | 'advanced'
  | 'statusBuilder'
  | 'statusCategory'
  | string;

export interface SelectedNode {
  type: SelectionTreeScope;
  name?: string;
}

/**
 * Core PCF object types that are always available in the mapping workspace.
 */
export const CORE_OBJECT_TYPES: string[] = [
  'ALL',
  'PIPELINE-REFERENCE',
  'END-POSITION-CLOSED',
  'END-POSITION-NULL',
  'PIPE',
  'WELD',
  'ELBOW',
  'SUPPORT',
  'END-CONNECTION-PIPELINE',
  'END-CONNECTION-EQUIPMENT',
  'FLANGE-BLIND',
  'GASKET',
  'FLANGE',
  'BOLT',
  'OLET',
  'MESSAGE-CIRCLE',
  'TEE',
  'VALVE',
  'FLOW-ARROW',
  'MESSAGE',
  'REDUCER-ECCENTRIC',
  'MESSAGE-DIAMOND',
  'ISO-SPLIT-POINT',
  'MESSAGE-ROUND',
  'INSULATION-SYMBOL',
  'LOCATION-POINT',
  'START-CO-ORDS',
  'FLOOR-SYMBOL',
  'ISO-MESSAGE',
  'FILTER',
  'CROSS',
  'UNION',
  'COUPLING',
  'REDUCER-CONCENTRIC',
  'MISC-COMPONENT',
  'INSTRUMENT',
  'LAPJOINT-STUBEND',
  'VALVE-3WAY',
  'VALVE-4WAY',
  'VALVE-ANGLE',
  'BEND',
  'MESSAGE-SQUARE',
  'MESSAGE-TRIANGLE',
  'MESSAGE-DOUBLE-CIRCLE',
  'MESSAGE-ELLIPSE',
  'BIP-IDENTIFIER',
  'CAP',
  'MISC-COMP',
  'SHOE',
  'REFERENCE-DIMENSION',
  'SO-SPLIT-POINT',
  'REFERENCE-DIMENSION-PRIME',
  'MESSAGE-DOUBLE',
  'TAP-CONNECTION',
  'REINFORCEMENT-PAD',
];

/**
 * Returns a human-readable label for the currently selected mapping scope.
 */
export function getSelectedScopeLabel(
  selectedNode: SelectedNode,
  statusCategoryName?: string
): string {
  switch (selectedNode.type) {
    case 'objectType':
      return selectedNode.name || 'Object type';
    case 'general':
      return 'General';
    case 'processConditions':
      return 'Process conditions';
    case 'projectProperties':
      return 'Project properties';
    case 'advanced':
      return 'Advanced settings';
    case 'statusBuilder':
      return 'Project phase statuses';
    case 'statusCategory':
      return statusCategoryName || 'Status category';
    default:
      return 'Workspace';
  }
}

/**
 * Determines whether a given node matches the active selection.
 */
export function isNodeSelected(selectedNode: SelectedNode, type: string, name?: string): boolean {
  if (name) {
    return selectedNode.type === type && selectedNode.name === name;
  }

  return selectedNode.type === type;
}

/**
 * Computes the set of object-type names that can be added to the workspace.
 *
 * Includes all core types plus any user-defined types already present,
 * excluding types that are already in the workspace.
 */
export function computeSelectableObjectTypes(objectTypes: ObjectType[]): string[] {
  const existingTypeNames = objectTypes.map((objectType) => objectType.name);
  const userDefinedTypes = existingTypeNames.filter(
    (name) => !CORE_OBJECT_TYPES.includes(name)
  );

  return [...CORE_OBJECT_TYPES, ...userDefinedTypes].filter(
    (name) => !existingTypeNames.includes(name)
  );
}

/**
 * Builds the confirmation message shown before deleting a user-defined object type.
 */
export function getDeleteObjectTypeMessage(objectTypeName: string): string {
  return `Delete object type "${objectTypeName}"?`;
}
